import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

const input = "w-full bg-[#0B0C0E] border border-[#2A2E39] rounded px-3 py-2 text-sm text-gray-100";

// One editable list (books or quotes) for the admin panel.
const EditList = ({ kind, fields, rows, reload }) => {
  const blank = Object.fromEntries(fields.map((f) => [f.key, f.def || ""]));
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const save = async () => {
    if (fields.some((f) => !f.optional && !String(form[f.key] || "").trim())) return toast.error("Fill in every field.");
    try {
      if (editId) await api.put(`/admin/mindset/${kind}/${editId}`, form);
      else await api.post(`/admin/mindset/${kind}`, form);
      toast.success(editId ? "Saved." : "Added.");
      setForm(blank); setEditId(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this for everyone?")) return;
    await api.delete(`/admin/mindset/${kind}/${id}`).catch(() => {});
    reload();
  };
  const list = rows.filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="mt-4" data-testid={`admin-mindset-${kind}`}>
      <div className="space-y-2">
        {fields.map((f) => (
          f.area
            ? <textarea key={f.key} rows={2} placeholder={f.label} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={input} data-testid={`admin-mindset-${kind}-${f.key}`} />
            : <input key={f.key} placeholder={f.label} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={input} data-testid={`admin-mindset-${kind}-${f.key}`} />
        ))}
        <div className="flex gap-2">
          <button onClick={save} className="hero-accent px-4 py-2 rounded text-sm font-bold" data-testid={`admin-mindset-${kind}-save`}>{editId ? "Save changes" : "Add"}</button>
          {editId && <button onClick={() => { setEditId(null); setForm(blank); }} className="border border-[#2A2E39] px-4 py-2 rounded text-sm" data-testid={`admin-mindset-${kind}-cancel`}>Cancel</button>}
        </div>
      </div>
      <input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className={`${input} mt-4`} data-testid={`admin-mindset-${kind}-search`} />
      <ul className="mt-2 max-h-80 overflow-y-auto divide-y divide-[#2A2E39]">
        {list.map((r) => (
          <li key={r.id} className="py-2 text-sm flex items-start gap-2" data-testid={`admin-mindset-${kind}-row-${r.id}`}>
            <span className="flex-1 text-gray-200">{r.title || `"${r.text}"`} <span className="text-gray-500">{r.author}{r.topic ? `, ${r.topic}` : ""}</span>
              {kind === "quotes" && <span className="block text-xs mt-0.5" data-testid={`admin-mindset-quotes-keyword-${r.id}`}>{r.keyword ? <>Red word: <span className="text-[#FD0F10] font-bold">{r.keyword}</span></> : <span className="text-gray-500">No red word yet</span>}</span>}
            </span>
            <button onClick={() => { setEditId(r.id); setForm(Object.fromEntries(fields.map((f) => [f.key, r[f.key] || ""]))); }} className="text-xs text-gray-300 hover:text-white" data-testid={`admin-mindset-${kind}-edit-${r.id}`}>Edit</button>
            <button onClick={() => remove(r.id)} className="text-xs text-[#FD0F10]" data-testid={`admin-mindset-${kind}-delete-${r.id}`}>Remove</button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const MindsetAdminCard = () => {
  const [tab, setTab] = useState("books");
  const [books, setBooks] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const load = () => {
    api.get("/mindset/books").then((r) => setBooks(r.data.books)).catch(() => {});
    api.get("/mindset/quotes").then((r) => setQuotes(r.data.quotes)).catch(() => {});
  };
  useEffect(load, []);
  const t = (k) => `px-3 py-1.5 rounded text-xs font-mono-ui uppercase ${tab === k ? "bg-[#FD0F10] text-white" : "border border-[#2A2E39] text-gray-300"}`;
  return (
    <div className="card-panel p-5" data-testid="admin-mindset">
      <div className="flex items-center justify-between">
        <div className="eyebrow">Mindset tab</div>
        <Link to="/admin/red-words" className="text-xs font-mono-ui uppercase tracking-wider text-[#FD0F10] hover:text-white" data-testid="admin-redwords-link">Red word review →</Link>
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={() => setTab("books")} className={t("books")} data-testid="admin-mindset-tab-books">Books ({books.length})</button>
        <button onClick={() => setTab("quotes")} className={t("quotes")} data-testid="admin-mindset-tab-quotes">Quotes ({quotes.length})</button>
      </div>
      {tab === "books"
        ? <EditList kind="books" rows={books} reload={load} fields={[{ key: "title", label: "Book title" }, { key: "author", label: "Author" }, { key: "about", label: "What it's about, in one line", area: true }]} />
        : <EditList kind="quotes" rows={quotes} reload={load} fields={[{ key: "text", label: "Quote, exactly as written", area: true }, { key: "author", label: "Author" }, { key: "topic", label: "Topic (work, money, discipline, belief, patience, action, gratitude, life)", def: "life" }, { key: "keyword", label: "Red word (one word from the quote, or leave empty)", optional: true }]} />}
    </div>
  );
};
